import { useMemo, useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { api } from '../lib/api'
import { getDateLocale, getLocalizedField, useSiteLang } from '../lib/localizedContent'
import { resolveMediaUrl } from '../lib/media'
import { Link } from 'react-router-dom'
import { FolderKanban, Calendar, Building2, ArrowRight, Search, X } from 'lucide-react'

interface Project {
  id: string
  title: string
  titleEN: string
  titleRU: string
  description: string
  descriptionEN: string
  descriptionRU: string
  imageUrl?: string
  industry_type?: string
  industry_typeRU?: string
  industry_typeEN?: string
  clientName?: string
  clientNameRU?: string
  clientNameEN?: string
  completedDate?: string
  isActive: boolean
}

const Projects = () => {
  const { t, i18n } = useTranslation()
  const dateLocale = getDateLocale(i18n.resolvedLanguage ?? i18n.language)
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [industryFilter, setIndustryFilter] = useState('all')
  const [clientFilter, setClientFilter] = useState('all')

  const ref = useRef(null)

  const lang = useSiteLang()

  useEffect(() => {
    loadProjects()
  }, [])

  const loadProjects = async () => {
    try {
      const data = await api.projects.getAll()
      setProjects(data.filter((p: Project) => p.isActive))
    } catch (err) {
      console.error('Error loading projects:', err)
      setError('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  const industryOptions = useMemo(() => {
    const values = new Set<string>()
    projects.forEach((project) => {
      if (project.industry_type) values.add(project.industry_type)
    })
    return Array.from(values).sort()
  }, [projects])


  const filteredProjects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return projects.filter((project) => {
      const title = getLocalizedField(project, 'title', lang).toLowerCase()
      const description = getLocalizedField(project, 'description', lang).toLowerCase()
      const clientName = (project.clientName || '').toLowerCase()

      const matchesSearch =
        !query ||
        title.includes(query) ||
        description.includes(query) ||
        clientName.includes(query)

      const matchesIndustry =
        industryFilter === 'all' || project.industry_type === industryFilter

      const matchesClient =
        clientFilter === 'all' || project.clientName === clientFilter

      return matchesSearch && matchesIndustry && matchesClient
    })
  }, [projects, searchTerm, industryFilter, clientFilter, lang])

  const hasActiveFilters =
    searchTerm.trim() !== '' || industryFilter !== 'all' || clientFilter !== 'all'

  const resetFilters = () => {
    setSearchTerm('')
    setIndustryFilter('all')
    setClientFilter('all')
  }

  if (loading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('projects.title')} <span className="gradient-text">{t('projects.title.projects')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('projects.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section ref={ref} className="section-padding bg-white">
        <div className="container-max">
          {projects.length > 0 && (
            <div className="mb-8 rounded-2xl border border-gray-100 bg-gray-50 p-4 md:p-6 min-w-0 overflow-hidden">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 min-w-0">
                <div className="md:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('projects.search')}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('projects.searchPlaceholder')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('projects.filterIndustry')}
                  </label>
                  <select
                    value={industryFilter}
                    onChange={(e) => setIndustryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('projects.filterIndustryAll')}</option>
                    {industryOptions.map((industry) => {
                      const sampleProject = projects.find((project) => project.industry_type === industry)
                      const label = sampleProject
                        ? getLocalizedField(sampleProject, 'industry_type', lang)
                        : industry

                      return (
                        <option key={industry} value={industry}>
                          {label}
                        </option>
                      )
                    })}
                  </select>
                </div>

              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  {t('projects.resultsCount', { count: filteredProjects.length })}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('projects.resetFilters')}
                  </button>
                )}
              </div>
            </div>
          )}

          {projects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-12"
            >
              <FolderKanban className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t('projects.zero')}</p>
              <p className="text-gray-500 mt-2">{t('projects.checkBack')}</p>
            </motion.div>
          ) : filteredProjects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-12"
            >
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t('projects.noResults')}</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
              >
                <X className="w-4 h-4 mr-1" />
                {t('projects.resetFilters')}
              </button>
            </motion.div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 w-full min-w-0">
              {filteredProjects.map((project, index) => {
                const imageSrc = resolveMediaUrl(project.imageUrl)
                const title = getLocalizedField(project, 'title', lang)
                const description = getLocalizedField(project, 'description', lang)
                const industryType = getLocalizedField(project, 'industry_type', lang)

                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08, duration: 0.5 }}
                    className="min-w-0 h-full"
                  >
                    <Link
                      to={`/project/${project.id}`}
                      className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-300 hover:border-primary-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                    >
                      <div className="border-b border-gray-100 bg-secondary-50 p-4 pb-3">
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                          <div className="flex items-center gap-1.5 border-b border-gray-200 bg-secondary-100 px-3 py-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-secondary-300" aria-hidden />
                            <span className="h-2.5 w-2.5 rounded-full bg-secondary-300" aria-hidden />
                            <span className="h-2.5 w-2.5 rounded-full bg-secondary-300" aria-hidden />
                            <span className="ml-2 h-2 flex-1 max-w-[8rem] rounded bg-white/80" aria-hidden />
                          </div>

                          <div className="relative aspect-[16/10] overflow-hidden bg-primary-50">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={title}
                                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <FolderKanban className="h-14 w-14 text-primary-300" />
                              </div>
                            )}

                            {industryType && (
                              <span className="absolute left-3 top-3 rounded-md bg-white/95 px-2.5 py-1 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-primary-100">
                                {industryType}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col p-5">
                        <h3 className="mb-2 break-words text-lg font-semibold text-gray-900 transition-colors group-hover:text-primary-700">
                          {title}
                        </h3>
                        <p className="mb-4 line-clamp-2 break-words text-sm leading-relaxed text-gray-600">
                          {description}
                        </p>

                        <div className="mt-auto flex items-end justify-between gap-3 border-t border-gray-100 pt-4">
                          <div className="min-w-0 space-y-1.5">
                            {project.clientName && (
                              <div className="flex items-center text-xs text-gray-500">
                                <Building2 className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 text-primary-600" />
                                <span className="truncate">{project.clientName}</span>
                              </div>
                            )}
                            {project.completedDate && (
                              <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 text-primary-600" />
                                <span>
                                  {new Date(project.completedDate).toLocaleDateString(dateLocale)}
                                </span>
                              </div>
                            )}
                          </div>

                          <span className="inline-flex flex-shrink-0 items-center text-sm font-medium text-primary-600 transition-colors group-hover:text-primary-700">
                            {t('projects.view')}
                            <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Projects
